import React, { useState } from "react";
import { useShow } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import { USER_ONE_QUERY, INVITE_USER_MUTATION, ACTIVATE_USER_MUTATION, DEACTIVATE_USER_MUTATION } from "./queries";
import { Typography, Button, Space, message, Tag } from "antd";
import { gqlClient } from "../../helpers/gqlProvider";

const { Text, Title } = Typography;

interface IUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  teamId?: string;
  appTeams?: {
    id: string;
    name: string;
  }[];
  organizationalRole?: string;
}

export const ShowUser: React.FC = () => {
  const { query } = useShow<IUser>({
    resource: "users",
    meta: {
      gqlQuery: USER_ONE_QUERY,
    },
  });

  const { data, isLoading } = query;
  const record = data?.data;
  const [loading, setLoading] = useState(false);

  const handleSendInvitation = async () => {
    if (!record) return;

    try {
      setLoading(true);
      const result = await gqlClient.mutation(INVITE_USER_MUTATION, {
        input: {
          email: record.email,
          teamId: record.teamId || "",
          organizationalRole: record.organizationalRole,
        }
      }).toPromise();

      if (result.error) {
        throw result.error;
      }

      message.success("Invitation sent successfully!");
    } catch (error) {
      console.error("Failed to send invitation:", error);
      message.error("Failed to send invitation: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!record) return;

    try {
      setLoading(true);
      const mutation = record.isActive ? DEACTIVATE_USER_MUTATION : ACTIVATE_USER_MUTATION;
      const result = await gqlClient.mutation(mutation, {
        input: { id: record.id }
      }).toPromise();

      if (result.error) {
        throw result.error;
      }

      const action = record.isActive ? "deactivated" : "activated";
      message.success(`User ${action} successfully!`);
      // Refresh the data
      query.refetch();
    } catch (error) {
      console.error("Failed to toggle user status:", error);
      message.error("Failed to toggle user status: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Show isLoading={isLoading}>
      <Title level={5}>Email</Title>
      <Text>{record?.email}</Text>

      <Title level={5}>Name</Title>
      <Text>{record?.firstName || record?.lastName ? (record?.firstName || "") + " " + (record?.lastName || "") : "-"}</Text>

      <Title level={5}>Team ID</Title>
      <Text>{record?.teamId || "-"}</Text>

      <Title level={5}>App Teams</Title>
      <Text>{record?.appTeams?.length ? record.appTeams.map(t => t.name).join(", ") : "-"}</Text>

      <Title level={5}>Organization Role</Title>
      <Text>{record?.organizationalRole || "-"}</Text>

      <Title level={5}>Status</Title>
      <Tag color={record?.isActive ? "green" : "red"}>
        {record?.isActive ? "Active" : "Inactive"}
      </Tag>

      <Title level={5}>Actions</Title>
      <Space>
        <Button
          type="primary"
          onClick={handleSendInvitation}
          loading={loading}
        >
          Send Invitation
        </Button>
        <Button
          onClick={handleToggleStatus}
          loading={loading}
          danger={record?.isActive}
        >
          {record?.isActive ? "Set Inactive" : "Set Active"}
        </Button>
      </Space>
    </Show>
  );
};